/**
 * CRUD for pipeline automation rules (SE-5506).
 *
 * Wraps the four generated mutations behind a clean interface, mirroring
 * `useTagCrud`. Every write refetches `PipelineAutomationRules` (refetch, not
 * optimism) so the panel's grouped list and the enable/pause state stay
 * authoritative — no partial optimistic write can flood error-code-13 the way
 * an incomplete `optimisticResponse` against this fragment would (SE-5058).
 *
 * The builder form is mapped to inputs by `utils/automationRuleInput`, which
 * PRESERVES `numericValue` as a decimal string end-to-end; this hook forwards
 * the built input untouched.
 * @module features/player-kanban/hooks/useAutomationRuleCrud
 */

import type { ApolloError } from "@apollo/client";
import { useCallback, useMemo } from "react";

import { AnalyticsTracker } from "@/features/analytics";
import {
  type PipelineAutomationRuleFragmentFragment,
  useCreatePipelineAutomationRuleMutation,
  useDeletePipelineAutomationRuleMutation,
  useSetPipelineAutomationRuleEnabledMutation,
  useUpdatePipelineAutomationRuleMutation,
} from "@/generated/graphql";

import type { AutomationRuleFormState } from "../types/automationRules";
import {
  buildCreateAutomationRuleInput,
  buildUpdateAutomationRuleInput,
} from "../utils/automationRuleInput";
import { classifyAutomationRuleKind } from "../utils/automationRuleKind";

/** Operation name whose refetch refreshes the panel list after any write. */
const RULES_OP = "PipelineAutomationRules";

/** Public API of {@link useAutomationRuleCrud}. */
export interface UseAutomationRuleCrudResult {
  /**
   * Create a new rule on the given board.
   * @param form - The builder form state.
   * @returns The created rule, or null if the mutation returned nothing.
   */
  readonly createRule: (
    form: AutomationRuleFormState
  ) => Promise<PipelineAutomationRuleFragmentFragment | null>;
  /**
   * Update an existing rule.
   * @param ruleId - The rule to edit.
   * @param form - The builder form state.
   * @returns The updated rule, or null.
   */
  readonly updateRule: (
    ruleId: string,
    form: AutomationRuleFormState
  ) => Promise<PipelineAutomationRuleFragmentFragment | null>;
  /**
   * Delete a rule.
   * @param ruleId - The rule to delete.
   */
  readonly deleteRule: (ruleId: string) => Promise<void>;
  /**
   * Enable or pause a rule (dedicated mutation — never reset by an edit).
   * @param ruleId - The rule to toggle.
   * @param isEnabled - True resumes, false pauses.
   * @returns The updated rule, or null.
   */
  readonly setRuleEnabled: (
    ruleId: string,
    isEnabled: boolean
  ) => Promise<PipelineAutomationRuleFragmentFragment | null>;
  /** True while a create is in flight. */
  readonly isCreating: boolean;
  /** True while an update is in flight. */
  readonly isUpdating: boolean;
  /** True while a delete is in flight. */
  readonly isDeleting: boolean;
  /** True while an enable/pause toggle is in flight. */
  readonly isTogglingEnabled: boolean;
  /** Combined error from any operation. */
  readonly error: ApolloError | undefined;
}

/**
 * Hook exposing create / update / delete / enable for automation rules.
 * @param kanbanBoardId - The pipeline the rules belong to (create scope +
 *   refetch variables).
 * @returns The {@link UseAutomationRuleCrudResult}.
 */
export const useAutomationRuleCrud = (
  kanbanBoardId: string
): UseAutomationRuleCrudResult => {
  const [createMutation, { loading: isCreating, error: createError }] =
    useCreatePipelineAutomationRuleMutation();
  const [updateMutation, { loading: isUpdating, error: updateError }] =
    useUpdatePipelineAutomationRuleMutation();
  const [deleteMutation, { loading: isDeleting, error: deleteError }] =
    useDeletePipelineAutomationRuleMutation();
  const [
    setEnabledMutation,
    { loading: isTogglingEnabled, error: setEnabledError },
  ] = useSetPipelineAutomationRuleEnabledMutation();

  const error = useMemo(
    () => createError || updateError || deleteError || setEnabledError,
    [createError, updateError, deleteError, setEnabledError]
  );

  const createRule = useCallback(
    async (form: AutomationRuleFormState) => {
      const { data } = await createMutation({
        variables: {
          input: buildCreateAutomationRuleInput(form, kanbanBoardId),
        },
        refetchQueries: [RULES_OP],
        awaitRefetchQueries: true,
      });
      const created = data?.createPipelineAutomationRule ?? null;

      // SE-5508 `rule_created`. Gated on the created rule being present: a
      // network/GraphQL failure throws out of the `await` above (this mutation
      // carries no hook-level `onError`, so it genuinely rejects), and a
      // resolved-but-empty response returns `null` — neither proves a rule was
      // created, so neither emits. Note the builder's `handleSave` treats
      // `null` as a success and toasts; this event deliberately does not.
      //
      // `trigger`/`action` are read off the SERVER's rule rather than the
      // form, so a Guard reports its on-schema encoding
      // (CARD_ENTERED_STAGE + ADD_TO_REVIEW_QUEUE) exactly as stored, and
      // `kind` uses the canonical `classifyAutomationRuleKind` derivation
      // rather than trusting the form's own flag.
      if (created) {
        void AnalyticsTracker.pipeline.logRuleCreated({
          kind: classifyAutomationRuleKind(created),
          trigger: created.triggerType,
          action: created.actionType,
          board_id: kanbanBoardId,
        });
      }
      return created;
    },
    [createMutation, kanbanBoardId]
  );

  const updateRule = useCallback(
    async (ruleId: string, form: AutomationRuleFormState) => {
      const { data } = await updateMutation({
        variables: {
          input: buildUpdateAutomationRuleInput(form, ruleId),
        },
        refetchQueries: [RULES_OP],
        awaitRefetchQueries: true,
      });
      return data?.updatePipelineAutomationRule ?? null;
    },
    [updateMutation]
  );

  const deleteRule = useCallback(
    async (ruleId: string) => {
      await deleteMutation({
        variables: { automationRuleId: ruleId },
        refetchQueries: [RULES_OP],
        awaitRefetchQueries: true,
      });
    },
    [deleteMutation]
  );

  const setRuleEnabled = useCallback(
    async (ruleId: string, isEnabled: boolean) => {
      const { data } = await setEnabledMutation({
        variables: { input: { automationRuleId: ruleId, isEnabled } },
        refetchQueries: [RULES_OP],
        awaitRefetchQueries: true,
      });
      return data?.setPipelineAutomationRuleEnabled ?? null;
    },
    [setEnabledMutation]
  );

  return useMemo(
    () => ({
      createRule,
      updateRule,
      deleteRule,
      setRuleEnabled,
      isCreating,
      isUpdating,
      isDeleting,
      isTogglingEnabled,
      error,
    }),
    [
      createRule,
      updateRule,
      deleteRule,
      setRuleEnabled,
      isCreating,
      isUpdating,
      isDeleting,
      isTogglingEnabled,
      error,
    ]
  );
};
